import React from "react";
import { Radio } from "antd";
import classes from "./CurrencySelectField.module.css";
import { CurrencyInfo } from "../../api/currency";
import { currencyCode } from "../../utils/formatters";
import useCurrencies from "../../hooks/useCurrencies";

/**
 * The currency an order is being registered in, as a first-class form field.
 *
 * ## Why it is a field and not an adornment
 *
 * This started life as a small toggle tucked onto the label row of the package-value input. It
 * worked, and operators did not see it — which on a screen where the choice silently re-prices the
 * whole order is the expensive kind of invisible. Standing it up as its own labelled field, sized
 * and aligned like every other input in the row, makes the choice visible without reintroducing
 * the alignment bug that putting it *above* a label caused.
 *
 * Renders nothing where orders may only be registered in one currency, so single-currency
 * deployments see the form exactly as they always have. That check lives in here rather than in the
 * caller for the same reason `DualMoney` owns its own: a caller that forgets it leaks the feature,
 * and a length check alone is not enough — Macedonia's supported set is [DEN, EUR], two entries,
 * but it does not trade in two currencies.
 */
export type CurrencySelectFieldProps = {
	label?: string;
	/** Only the currencies this deployment actually trades in. Never the raw Currency/list. */
	currencies: CurrencyInfo[];
	value?: number;
	onChange: (currencyId: number) => void;
	/**
	 * Locks the choice without hiding it.
	 *
	 * Used where something else on the form decides the currency — the destination country, on
	 * Register order. The options stay on screen, greyed, with the forced one selected, and the
	 * `hint` says why: a field that vanishes reads as "this order has no currency", and the
	 * operator is left to infer which one from the symbol on the amount input.
	 */
	disabled?: boolean;
	/** A line below the field, height reserved so selecting does not shift the layout. */
	hint?: React.ReactNode;
	className?: string;
};

const CurrencySelectField: React.FC<CurrencySelectFieldProps> = ({
	label,
	currencies,
	value,
	onChange,
	disabled,
	hint,
	className,
}) => {
	const { dualCurrencyEnabled } = useCurrencies();

	if (!dualCurrencyEnabled || currencies.length < 2) return null;

	return (
		<div className={`${classes.field} ${className ?? ""}`}>
			<label className={classes.label}>{label}</label>
			<Radio.Group
				className={classes.group}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				optionType="button"
				buttonStyle="solid"
			>
				{currencies.map((currency) => (
					<Radio.Button
						className={classes.option}
						key={currency.id}
						value={currency.id}
						title={currency.description ?? undefined}
					>
						<span className={classes.symbol}>
							{currency.symbol ?? currency.description}
						</span>
						<span className={classes.code}>
							{currencyCode(currency.id)}
						</span>
					</Radio.Button>
				))}
			</Radio.Group>
			{hint}
		</div>
	);
};

export default CurrencySelectField;
